import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import { drawBannerBg } from "../entities/BannerAccessory";
import { drawCharacterSprite } from "../entities/CharacterSprite";
import { dominantColor, drawCustomGrid } from "../entities/CustomArt";
import { drawExplosion } from "../entities/Explosion";
import { drawFrame } from "../entities/FrameAccessory";
import { drawHatBack, drawHatFront } from "../entities/HatAccessory";
import { drawTrailIcon } from "../entities/TrailIcon";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { customIdFor, type CustomSlot } from "../systems/CustomStore";
import type { Renderer } from "../Renderer";
import type { GameContext, Scene } from "./Scene";
import { ShopScene } from "./ShopScene";

// Curated palette — bright, readable picks plus black/white/grey. The last entry
// (empty string) is the eraser (transparent).
const PALETTE: string[] = [
  "#000000", "#ffffff", "#9e9e9e", "#5d4037",
  "#e53935", "#ff7043", "#fbc02d", "#fff176",
  "#43a047", "#1de9b6", "#26c6da", "#1e88e5",
  "#3949ab", "#8e24aa", "#ec407a", "#f8bbd0",
];

const SLOT_LABEL: Record<CustomSlot, string> = {
  character: "Character",
  hat: "Hat",
  banner: "Banner",
  trail: "Trail",
  frame: "Frame",
  background: "Scenery",
  explosion: "Explosion",
};

const GRID_X = 52;
const GRID_Y = 150;
const GRID_SIZE = 256;

const PREVIEW = { x: VIRTUAL_WIDTH / 2 - 30, y: 70, w: 60, h: 60 };

export class CustomEditorScene implements Scene {
  private readonly renderer: Renderer;
  private cells: string[];
  private readonly grid: number;
  private color = PALETTE[4]!;
  private painting = false;
  private animated: boolean;
  /** Only banners and frames offer an animation toggle. */
  private readonly canAnimate: boolean;
  /** Frame only: up to 6 colors the animated border flows through ("" = empty). */
  private readonly animColors: string[];

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly slot: CustomSlot,
  ) {
    this.renderer = ctxMgr.renderer;
    const design = ctxMgr.shop.custom.design(slot);
    const d = ctxMgr.shop.custom.cellsOrBlank(slot);
    this.cells = d.cells;
    this.grid = d.grid;
    this.canAnimate = slot === "banner" || slot === "frame";
    this.animated = design?.animated ?? false;
    const existing = design?.animColors ?? [];
    this.animColors = Array.from({ length: 6 }, (_, i) => existing[i] ?? "");
  }

  enter(): void {
    this.rebuild();
    const canvas = this.renderer.canvas;
    canvas.addEventListener("pointerdown", this.onDown);
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
  }

  exit(): void {
    this.ctxMgr.buttons.clear();
    const canvas = this.renderer.canvas;
    canvas.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
  }

  update(_dt: number): void {
    // static
  }

  // --- painting ---

  private cellAt(clientX: number, clientY: number): number | null {
    const { x, y } = this.renderer.clientToCanvas(clientX, clientY);
    if (x < GRID_X || x >= GRID_X + GRID_SIZE || y < GRID_Y || y >= GRID_Y + GRID_SIZE) return null;
    const cell = GRID_SIZE / this.grid;
    const col = Math.floor((x - GRID_X) / cell);
    const row = Math.floor((y - GRID_Y) / cell);
    return row * this.grid + col;
  }

  private paint(clientX: number, clientY: number): void {
    const idx = this.cellAt(clientX, clientY);
    if (idx === null) return;
    this.cells[idx] = this.color;
  }

  private readonly onDown = (e: PointerEvent): void => {
    const idx = this.cellAt(e.clientX, e.clientY);
    if (idx === null) return;
    this.painting = true;
    this.cells[idx] = this.color;
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (!this.painting) return;
    this.paint(e.clientX, e.clientY);
  };

  private readonly onUp = (): void => {
    this.painting = false;
  };

  // --- actions ---

  private toggleAnimColor(i: number): void {
    // Tap with a color to assign that slot; tap with the eraser to clear it.
    this.animColors[i] = this.color;
  }

  private save(): void {
    const animColors = this.animColors.filter((c) => c);
    this.ctxMgr.shop.custom.setDesign(this.slot, this.cells, this.grid, this.animated, animColors);
    // Auto-equip so the new design shows up immediately.
    void this.ctxMgr.shop.equip(this.slot, customIdFor(this.slot));
    this.goBack();
  }

  private clearGrid(): void {
    if (!window.confirm("Clear the whole drawing?")) return;
    this.cells = new Array(this.grid * this.grid).fill("");
  }

  private goBack(): void {
    const shop = new ShopScene(this.ctxMgr);
    this.ctxMgr.goTo(shop);
  }

  private rebuild(): void {
    const buttons: Button[] = [];

    // back / cancel
    buttons.push(backButton(() => this.goBack()));

    // palette swatches — 8 per row, 2 rows
    const cols = 8;
    const sw = 30;
    const gap = 6;
    const totalW = cols * sw + (cols - 1) * gap;
    const startX = (VIRTUAL_WIDTH - totalW) / 2;
    const startY = GRID_Y + GRID_SIZE + 16;
    PALETTE.forEach((c, i) => {
      const cx = startX + (i % cols) * (sw + gap);
      const cy = startY + Math.floor(i / cols) * (sw + gap);
      buttons.push({
        x: cx, y: cy, w: sw, h: sw,
        draw: (ctx) => this.drawSwatchButton(ctx, c, cx, cy, sw),
        onPress: () => {
          this.color = c;
        },
      });
    });

    let y = startY + 2 * (sw + gap) + 4;

    // Animation-colour slots (frames only, when animated): tap a slot to set it
    // to the current paint color; tap with the eraser selected to clear it.
    if (this.slot === "frame" && this.animated) {
      const ss = 26;
      const sg = 6;
      const stripX = startX + 46;
      for (let i = 0; i < this.animColors.length; i += 1) {
        const cx = stripX + i * (ss + sg);
        const sy = y;
        buttons.push({
          x: cx, y: sy, w: ss, h: ss,
          draw: (ctx) => this.drawAnimSlot(ctx, i, cx, sy, ss),
          onPress: () => this.toggleAnimColor(i),
        });
      }
      y += ss + 8;
    }

    // eraser / clear / animate toggle
    const eraser = { x: startX, y, w: 70, h: 26 };
    buttons.push({
      ...eraser,
      draw: (ctx, hit) => drawTextButton(ctx, "Eraser", eraser, hit, this.color === ""),
      onPress: () => {
        this.color = "";
      },
    });
    const clearBtn = { x: startX + 78, y, w: 70, h: 26 };
    buttons.push({
      ...clearBtn,
      draw: (ctx, hit) => drawTextButton(ctx, "Clear", clearBtn, hit, false),
      onPress: () => this.clearGrid(),
    });

    // Banners and frames can animate — a toggle to the right of Clear.
    if (this.canAnimate) {
      const animBtn = { x: startX + 156, y, w: totalW - 156, h: 26 };
      buttons.push({
        ...animBtn,
        draw: (ctx, hit) =>
          drawTextButton(ctx, this.animated ? "Animated: ON" : "Animated: off", animBtn, hit, this.animated),
        onPress: () => {
          this.animated = !this.animated;
          this.rebuild(); // show/hide the animation-colour strip
        },
      });
    }
    y += 32;

    // Save / Cancel
    const saveBtn = { x: startX, y, w: totalW / 2 - 4, h: 34 };
    buttons.push({
      ...saveBtn,
      draw: (ctx, hit) => drawPrimaryButton(ctx, "Save & Equip", saveBtn, hit),
      onPress: () => this.save(),
    });
    const cancelBtn = { x: startX + totalW / 2 + 4, y, w: totalW / 2 - 4, h: 34 };
    buttons.push({
      ...cancelBtn,
      draw: (ctx, hit) => drawTextButton(ctx, "Cancel", cancelBtn, hit, false),
      onPress: () => this.goBack(),
    });

    this.ctxMgr.buttons.set(buttons);
  }

  private drawAnimSlot(
    ctx: CanvasRenderingContext2D,
    i: number,
    x: number,
    y: number,
    size: number,
  ): void {
    if (i === 0) {
      ctx.fillStyle = "rgba(245,233,200,0.85)";
      ctx.font = "10px system-ui, sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText("Anim", x - 6, y + size / 2);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
    }
    const color = this.animColors[i];
    if (color) {
      ctx.fillStyle = color;
      fillRoundedRect(ctx, x, y, size, size, 5);
      ctx.strokeStyle = "#7a5e2a";
      ctx.lineWidth = 1.5;
      strokeRoundedRect(ctx, x + 0.5, y + 0.5, size - 1, size - 1, 5);
      return;
    }
    ctx.fillStyle = "#0e1118";
    fillRoundedRect(ctx, x, y, size, size, 5);
    ctx.strokeStyle = "rgba(245,233,200,0.4)";
    ctx.lineWidth = 1;
    strokeRoundedRect(ctx, x + 0.5, y + 0.5, size - 1, size - 1, 5);
    ctx.fillStyle = "rgba(245,233,200,0.5)";
    ctx.font = "13px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("+", x + size / 2, y + size / 2 + 0.5);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  private drawSwatchButton(
    ctx: CanvasRenderingContext2D,
    color: string,
    x: number,
    y: number,
    size: number,
  ): void {
    ctx.fillStyle = color || "#1a1f2b";
    fillRoundedRect(ctx, x, y, size, size, 5);
    const selected = this.color === color;
    ctx.strokeStyle = selected ? "#ffffff" : "#7a5e2a";
    ctx.lineWidth = selected ? 2.5 : 1.5;
    strokeRoundedRect(ctx, x + 0.5, y + 0.5, size - 1, size - 1, 5);
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    ctx.fillStyle = "#1a1f2b";
    ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);

    ctx.fillStyle = "#f5e9c8";
    ctx.font = "bold 18px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`Custom ${SLOT_LABEL[this.slot]}`, VIRTUAL_WIDTH / 2, 34);

    this.renderPreview(ctx);

    // grid background
    ctx.fillStyle = "#0e1118";
    fillRoundedRect(ctx, GRID_X - 4, GRID_Y - 4, GRID_SIZE + 8, GRID_SIZE + 8, 6);

    // checkerboard for transparent cells, then painted cells
    const cell = GRID_SIZE / this.grid;
    for (let row = 0; row < this.grid; row += 1) {
      for (let col = 0; col < this.grid; col += 1) {
        const px = GRID_X + col * cell;
        const py = GRID_Y + row * cell;
        const color = this.cells[row * this.grid + col];
        if (color) {
          ctx.fillStyle = color;
        } else {
          ctx.fillStyle = (row + col) % 2 === 0 ? "#23262e" : "#2c2f38";
        }
        ctx.fillRect(px, py, cell, cell);
      }
    }

    // grid lines
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1;
    for (let i = 0; i <= this.grid; i += 1) {
      const p = i * cell;
      ctx.beginPath();
      ctx.moveTo(GRID_X + p, GRID_Y);
      ctx.lineTo(GRID_X + p, GRID_Y + GRID_SIZE);
      ctx.moveTo(GRID_X, GRID_Y + p);
      ctx.lineTo(GRID_X + GRID_SIZE, GRID_Y + p);
      ctx.stroke();
    }

    ctx.fillStyle = "rgba(245,233,200,0.7)";
    ctx.font = "10px system-ui, sans-serif";
    const hint =
      this.slot === "frame"
        ? "draw the corners · set border colours below"
        : "drag to draw · pick a color below";
    ctx.fillText(hint, VIRTUAL_WIDTH / 2, GRID_Y + GRID_SIZE + 12);
  }

  // Live preview of how the drawing reads in its slot.
  private renderPreview(ctx: CanvasRenderingContext2D): void {
    const { x, y, w, h } = PREVIEW;
    const time = performance.now() / 1000;
    ctx.save();
    ctx.fillStyle = "#0e1118";
    fillRoundedRect(ctx, x, y, w, h, 8);
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    const anim = { bobTime: time, flapTime: 0 };
    const hatAnim = { propAngle: time * 12, bobTime: time, flapTime: 0 };
    if (this.slot === "character") {
      ctx.translate(x + w / 2, y + h / 2);
      ctx.scale(1.4, 1.4);
      drawCharacterSprite(ctx, 14, { kind: "custom", cells: this.cells, grid: this.grid }, anim);
    } else if (this.slot === "hat") {
      ctx.translate(x + w / 2, y + h * 0.62);
      ctx.scale(1.2, 1.2);
      drawCharacterSprite(
        ctx,
        14,
        { kind: "lizard", body: "#4caf50", belly: "#aed581", tail: "#388e3c", legs: "#2e7d32" },
        anim,
      );
      drawHatBack(ctx, 14, { kind: "custom", cells: this.cells, grid: this.grid }, hatAnim);
      drawHatFront(ctx, 14, { kind: "custom", cells: this.cells, grid: this.grid }, hatAnim);
    } else if (this.slot === "banner") {
      drawBannerBg(
        ctx,
        { kind: "custom", a: dominantColor(this.cells), cells: this.cells, grid: this.grid, animated: this.animated },
        x,
        y + h / 2 - 14,
        w,
        28,
        time,
      );
    } else if (this.slot === "trail") {
      const cfg = {
        kind: "custom" as const,
        color: dominantColor(this.cells),
        sizeMin: 3,
        sizeMax: 5,
        vy: -8,
        lifeMin: 0.5,
        lifeMax: 0.8,
        ratePerSec: 14,
        cells: this.cells,
        grid: this.grid,
      };
      for (let i = 0; i < 4; i += 1) {
        drawTrailIcon(ctx, cfg, x + w - 10 - i * 12, y + h / 2 + (i % 2 ? -3 : 3), 1 - i * 0.2);
      }
      ctx.globalAlpha = 1;
    } else if (this.slot === "frame") {
      ctx.fillStyle = "#f5e9c8";
      fillRoundedRect(ctx, x + 10, y + 10, w - 20, h - 20, 6);
      drawFrame(
        ctx,
        {
          kind: "custom",
          color: dominantColor(this.cells),
          width: 3,
          cells: this.cells,
          grid: this.grid,
          animated: this.animated,
          animColors: this.animColors.filter((c) => c),
        },
        { x: x + 8, y: y + 8, w: w - 16, h: h - 16, radius: 6 },
        time,
      );
    } else if (this.slot === "explosion") {
      // looping burst of the drawing
      const loop = 0.9;
      const p = ((performance.now() / 1000) % loop) / loop;
      ctx.translate(x + w / 2, y + h / 2);
      ctx.scale(0.7, 0.7);
      drawExplosion(
        ctx,
        { kind: "custom", color: dominantColor(this.cells), cells: this.cells, grid: this.grid },
        0,
        0,
        p,
        [],
      );
    } else {
      // background — show the raw drawing stretched
      drawCustomGrid(ctx, this.cells, this.grid, x, y, w, h);
    }
    ctx.restore();
  }
}

function drawTextButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  rect: { x: number; y: number; w: number; h: number },
  hit: boolean,
  active: boolean,
): void {
  ctx.fillStyle = active || hit ? "#7a5e2a" : "rgba(245,233,200,0.9)";
  fillRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 6);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 1.5;
  strokeRoundedRect(ctx, rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1, 6);
  ctx.fillStyle = active || hit ? "#f5e9c8" : "#1a1f2b";
  ctx.font = "bold 13px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
  ctx.textBaseline = "alphabetic";
}

function drawPrimaryButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  rect: { x: number; y: number; w: number; h: number },
  hit: boolean,
): void {
  ctx.fillStyle = hit ? "#1a1f2b" : "#2e7d32";
  fillRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 6);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 13px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
  ctx.textBaseline = "alphabetic";
}
